import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Loader2, PlugZap, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur } from "@/lib/invoice";
import { useAuth } from "@/context/AuthContext";

const ENVIRONMENTS = ["sandbox", "production"];

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [company, setCompany] = useState({ name: "", siret: "", vat_number: "", address: "", pdp_platform: "" });
  const [pdp, setPdp] = useState({ provider: "", environment: "sandbox", client_id: "", client_secret: "", customer_id: "" });
  const [pdpPlatforms, setPdpPlatforms] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/company").then(({ data }) => setCompany((c) => ({ ...c, ...(data?.company || data) }))).catch(() => {});
    api.get("/pdp/config").then(({ data }) => setPdp((p) => ({ ...p, ...(data?.config || data) }))).catch(() => {});
    api.get("/plans").then(({ data }) => setPdpPlatforms(data?.pdp_platforms || [])).catch(() => {});
    api.get("/subscription").then(({ data }) => setSubscription(data)).catch(() => {});
    api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  const save = async (key, fn, msg) => {
    setSaving(key);
    try {
      await fn();
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving("");
    }
  };

  const testPdp = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/pdp/test");
      toast.success(data?.message || "Connexion PDP réussie");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du test PDP");
    } finally {
      setTesting(false);
    }
  };

  const resetAccount = async () => {
    setResetting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Compte supprimé — reconnectez-vous avec Google pour repartir de zéro", { duration: 8000 });
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Réinitialisation impossible");
      setResetting(false);
    }
  };

  const [pwd, setPwd] = useState({ password: "", confirm: "" });

  const savePassword = async () => {
    if (pwd.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (pwd.password !== pwd.confirm) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setSaving("password");
    try {
      await api.post("/auth/set-password", { password: pwd.password });
      toast.success("Mot de passe créé — vous pouvez désormais vous connecter par e-mail + mot de passe", { duration: 6000 });
      setPwd({ password: "", confirm: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de définir le mot de passe");
    } finally {
      setSaving("");
    }
  };

  const inputCls = "w-full rounded-xl bg-black/40 border border-white/10 px-4 py-2.5 text-sm placeholder:text-zinc-600 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none transition-[border-color,box-shadow] duration-200";

  return (
    <div data-testid="settings-page" className="max-w-4xl space-y-10">
      <div>
        <p className="overline-label mb-2">Paramètres</p>
        <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl">Configuration</h1>
      </div>

      {/* Entreprise */}
      <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6 md:p-8" data-testid="company-section">
        <h2 className="font-display text-lg mb-6">Profil entreprise</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Raison sociale"><input data-testid="company-name" className={inputCls} value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} /></Field>
          <Field label="SIRET"><input data-testid="company-siret" className={`${inputCls} font-mono`} value={company.siret || ""} onChange={(e) => setCompany({ ...company, siret: e.target.value })} /></Field>
          <Field label="N° TVA intracommunautaire"><input data-testid="company-vat" className={`${inputCls} font-mono`} value={company.vat_number || ""} onChange={(e) => setCompany({ ...company, vat_number: e.target.value })} /></Field>
          <Field label="Plateforme PDP">
            <select data-testid="company-pdp" className={inputCls} value={company.pdp_platform || ""} onChange={(e) => setCompany({ ...company, pdp_platform: e.target.value })}>
              <option value="">Sélectionner…</option>
              {pdpPlatforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Adresse" full>
            <textarea data-testid="company-address" rows={3} className={inputCls} value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
          </Field>
        </div>
        <SaveBtn testid="company-save" busy={saving === "company"} onClick={() => save("company", () => api.put("/company", company), "Profil entreprise enregistré")} />
      </section>

      {/* PDP */}
      <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6 md:p-8" data-testid="pdp-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-lg">Connexion PDP</h2>
          <button
            onClick={testPdp}
            disabled={testing}
            data-testid="pdp-test-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors duration-200 disabled:opacity-50"
          >
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />}
            Tester la connexion
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Fournisseur"><input data-testid="pdp-provider" className={inputCls} value={pdp.provider || ""} onChange={(e) => setPdp({ ...pdp, provider: e.target.value })} placeholder="ex. Pennylane PDP" /></Field>
          <Field label="Environnement">
            <select data-testid="pdp-environment" className={inputCls} value={pdp.environment || "sandbox"} onChange={(e) => setPdp({ ...pdp, environment: e.target.value })}>
              {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
            </select>
          </Field>
          <Field label="Client ID"><input data-testid="pdp-client-id" className={`${inputCls} font-mono`} value={pdp.client_id || ""} onChange={(e) => setPdp({ ...pdp, client_id: e.target.value })} /></Field>
          <Field label="Client secret"><input data-testid="pdp-client-secret" type="password" className={`${inputCls} font-mono`} value={pdp.client_secret || ""} onChange={(e) => setPdp({ ...pdp, client_secret: e.target.value })} /></Field>
          <Field label="Customer ID"><input data-testid="pdp-customer-id" className={`${inputCls} font-mono`} value={pdp.customer_id || ""} onChange={(e) => setPdp({ ...pdp, customer_id: e.target.value })} /></Field>
        </div>
        <SaveBtn testid="pdp-save" busy={saving === "pdp"} onClick={() => save("pdp", () => api.put("/pdp/config", pdp), "Configuration PDP enregistrée")} />
      </section>

      {/* Abonnement */}
      <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6 md:p-8" data-testid="subscription-section">
        <h2 className="font-display text-lg mb-6">Abonnement & usage</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-white/10 p-5">
            <p className="overline-label mb-2">Plan actuel</p>
            <p className="font-display text-xl" data-testid="current-plan">{subscription?.plan || subscription?.tier || "—"}</p>
          </div>
          <div className="rounded-xl border border-white/10 p-5">
            <p className="overline-label mb-2">Statut</p>
            <p className="font-display text-xl capitalize" data-testid="subscription-status">{subscription?.status || "—"}</p>
          </div>
          <div className="rounded-xl border border-white/10 p-5">
            <p className="overline-label mb-2">Conversions utilisées</p>
            <p className="font-display text-xl font-mono" data-testid="usage-count">
              {usage ? `${usage.used ?? usage.conversions ?? 0}${(usage.quota ?? usage.limit) ? ` / ${usage.quota ?? usage.limit}` : ""}` : "—"}
            </p>
          </div>
        </div>
        {subscription?.amount && (
          <p className="mt-4 text-sm text-zinc-400">Facturation : {eur(subscription.amount)} / mois</p>
        )}
        <a
          href="/app/abonnement"
          data-testid="settings-manage-subscription"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors duration-200"
        >
          Gérer mon abonnement
        </a>
      </section>

      {/* Sécurité */}
      <section className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6 md:p-8" data-testid="security-section">
        <h2 className="font-display text-lg mb-2">Sécurité</h2>
        <p className="text-sm text-zinc-400 mb-6 max-w-xl">
          Créez un mot de passe pour vous connecter aussi par e-mail, sans passer par Google.
          Il sera valable sur le web et sur l'app mobile. Minimum 8 caractères.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nouveau mot de passe">
            <input
              type="password"
              data-testid="password-new"
              autoComplete="new-password"
              className={inputCls}
              value={pwd.password}
              onChange={(e) => setPwd({ ...pwd, password: e.target.value })}
              placeholder="8 caractères minimum"
            />
          </Field>
          <Field label="Confirmation">
            <input
              type="password"
              data-testid="password-confirm"
              autoComplete="new-password"
              className={inputCls}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              placeholder="Répétez le mot de passe"
            />
          </Field>
        </div>
        {pwd.confirm.length > 0 && pwd.password !== pwd.confirm && (
          <p className="mt-3 text-sm text-[#FF3B30]" data-testid="password-mismatch">Les deux mots de passe ne correspondent pas.</p>
        )}
        <SaveBtn testid="password-save" busy={saving === "password"} onClick={savePassword} />
      </section>

      {/* Zone dangereuse */}
      <section className="rounded-2xl border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6 md:p-8" data-testid="danger-zone">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FF3B30]/15 text-[#FF3B30]">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h2 className="font-display text-lg text-[#FF3B30]">Zone dangereuse</h2>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-xl">
              La remise à zéro <strong className="text-white">supprime définitivement votre compte et toutes vos données</strong> :
              factures, archives, réglages — sur le web <em>et</em> l'app mobile (base partagée).
              Action irréversible. Après suppression, reconnectez-vous avec Google : un compte vierge sera créé.
            </p>
            {!confirmReset ? (
              <button
                onClick={() => setConfirmReset(true)}
                data-testid="reset-account-button"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#FF3B30]/50 text-[#FF3B30] px-5 py-2.5 text-sm font-medium hover:bg-[#FF3B30]/10 transition-colors duration-200"
              >
                <Trash2 className="h-4 w-4" />
                Remettre mon compte à zéro
              </button>
            ) : (
              <div className="mt-5 rounded-xl border border-[#FF3B30]/40 bg-black/40 p-5" data-testid="reset-confirm-panel">
                <p className="text-sm text-white font-medium mb-4">
                  Confirmez-vous la suppression définitive du compte ? Cette action est irréversible.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={resetAccount}
                    disabled={resetting}
                    data-testid="reset-confirm-button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#ff554c] transition-colors duration-200 disabled:opacity-50"
                  >
                    {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Oui, supprimer définitivement
                  </button>
                  <button
                    onClick={() => setConfirmReset(false)}
                    data-testid="reset-cancel-button"
                    className="rounded-full border border-white/20 px-5 py-2.5 text-sm text-zinc-300 hover:border-white/50 transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, full, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="overline-label block mb-2">{label}</label>
    {children}
  </div>
);

const SaveBtn = ({ testid, busy, onClick }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testid}
    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200 disabled:opacity-50"
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
    Enregistrer
  </button>
);
